import { IonButton, IonItem, IonLabel, IonList, IonSpinner, IonToast } from "@ionic/react";
import { useFormik } from "formik";
import React, { useEffect, useState } from "react";
import * as Yup from "yup";
import ChannelPartnerFields from "./ChannelPartnerFields";
import ChannelPartnerMediaUploads from "./ChannelPartnerMediaUploads";
import RetailerOnboardingQr from "./RetailerOnboardingQr";
import {
  getInventoryLocationByIdFull,
  InventoryLocationPartnerType,
  patchInventoryLocation,
} from "../services/inventoryLocation.service";
import { getUser } from "../services/user.service";
import { Regex } from "../services/common.service";

// Same field requirements as CreateInventoryLocation's schema, minus the
// owner-identity rules -- the owner isn't editable from this screen (see
// the component doc below), so there's nothing to validate there.
export function buildEditInventoryLocationValidationSchema(isDistributor: boolean) {
  return Yup.object({
    businessName: Yup.string().trim().required("Required"),
    parentId: Yup.string().when([], {
      is: () => !isDistributor,
      then: Yup.string().required("Select the parent distributor"),
    }),
    socialHandle: Yup.string().trim().matches(Regex.socialHandle, {
      message: "Letters, numbers, periods and underscores only -- no spaces, and can't start/end with or repeat a period",
      excludeEmptyString: true,
    }),
    billingAddress: Yup.string().trim().required("Required"),
    shippingAddress: Yup.string().trim().required("Required"),
    geoLocation: Yup.mixed().required("Tap the location icon to capture the shop's GPS location"),
  });
}

// Edit an existing channel-partner InventoryLocation -- a deliberately
// separate component from CreateInventoryLocation.tsx rather than one
// component branching on an isEdit flag, since edit mode differs on three
// axes at once (no owner creation/search, no two-phase "create then attach
// media" gating, isDistributor comes from the fetched record instead of
// route state). Reuses ChannelPartnerFields/ChannelPartnerMediaUploads so
// the actual field UI only lives in one place.
//
// Owner reassignment and Distributor<->Retailer conversion are explicitly
// out of scope here -- the owner is shown read-only, and isDistributor is
// fixed from the fetched record.
interface Props {
  locationId: string;
}

const EditInventoryLocation: React.FC<Props> = ({ locationId }) => {
  const [loaded, setLoaded] = useState(false);
  const [isDistributor, setIsDistributor] = useState(false);
  const [ownerName, setOwnerName] = useState("");
  const [ownerMobile, setOwnerMobile] = useState("");
  // Snapshot of the saved WhatsApp No. at load time -- the QR below encodes
  // this, not formik.values.whatsappNo, so it can't reflect an in-progress,
  // unsaved edit to that field. Re-fetched after every successful save (see
  // onSubmit) so it stays in sync once a change is actually persisted.
  const [savedWhatsappNo, setSavedWhatsappNo] = useState("");
  const [existingPhotoUrl, setExistingPhotoUrl] = useState("");
  const [existingGstUrl, setExistingGstUrl] = useState("");
  const [saving, setSaving] = useState(false);
  const [onSuccess, setOnSuccess] = useState("");
  const [onError, setOnError] = useState("");

  const formik = useFormik({
    initialValues: {
      businessName: "",
      whatsappNo: "",
      email: "",
      gstNo: "",
      socialHandle: "",
      parentId: "",
      parentName: "",
      billingAddress: "",
      shippingAddress: "",
      village: "",
      subDistrict: "",
      district: "",
      state: "",
      country: "",
      geoLocation: undefined as any,
    },
    validationSchema: buildEditInventoryLocationValidationSchema(isDistributor),
    onSubmit: async (values) => {
      setSaving(true);
      try {
        const payload: any = {
          name: values.businessName,
          businessName: values.businessName,
          whatsappNo: values.whatsappNo || undefined,
          email: values.email || undefined,
          gstNo: values.gstNo || undefined,
          instagramHandle: values.socialHandle || undefined,
          facebookHandle: values.socialHandle || undefined,
          village: values.village || undefined,
          subDistrict: values.subDistrict || undefined,
          district: values.district || undefined,
          state: values.state || undefined,
          country: values.country || undefined,
          geoLocation: values.geoLocation,
          billingAddress: values.billingAddress,
          shippingAddress: values.shippingAddress,
        };
        if (!isDistributor) {
          payload.parentId = values.parentId;
        }
        // patchInventoryLocation already runs the response through
        // handleErrors internally -- it resolves to undefined on a 204 (the
        // common case for a PATCH here), so chaining another handleErrors
        // call on top of that would throw on `undefined.ok` even though the
        // save succeeded.
        await patchInventoryLocation(locationId, payload);
        setSavedWhatsappNo(values.whatsappNo);
        setOnSuccess("Changes saved");
      } catch (err: any) {
        setOnError(err?.message ?? String(err));
      } finally {
        setSaving(false);
      }
    },
  });

  useEffect(() => {
    let cancelled = false;
    async function load() {
      const data = await getInventoryLocationByIdFull(locationId);
      const distributor = data.partnerType === InventoryLocationPartnerType.DISTRIBUTOR;

      const ownerId = data.owner?.[0];
      const owner = ownerId ? await getUser(ownerId).catch(() => undefined) : undefined;

      let parentName = "";
      if (!distributor && data.parentId) {
        const parent = await getInventoryLocationByIdFull(data.parentId).catch(() => undefined);
        parentName = parent?.businessName ?? parent?.name ?? "";
      }

      if (cancelled) return;
      setIsDistributor(distributor);
      setOwnerName(owner?.name ?? "");
      setOwnerMobile(owner ? `${owner.countryCode ?? ""} ${owner.mobile ?? ""}`.trim() : "");
      formik.setValues({
        businessName: data.businessName ?? data.name ?? "",
        whatsappNo: data.whatsappNo ?? "",
        email: data.email ?? "",
        gstNo: data.gstNo ?? "",
        socialHandle: data.instagramHandle ?? data.facebookHandle ?? "",
        parentId: data.parentId ?? "",
        parentName,
        billingAddress: data.billingAddress ?? "",
        shippingAddress: data.shippingAddress ?? "",
        village: data.village ?? "",
        subDistrict: data.subDistrict ?? "",
        district: data.district ?? "",
        state: data.state ?? "",
        country: data.country ?? "",
        geoLocation: data.geoLocation,
      });
      setExistingPhotoUrl(data.shopFrontPhotoUrl ?? "");
      setExistingGstUrl(data.gstCertificateUrl ?? "");
      setSavedWhatsappNo(data.whatsappNo ?? "");
      setLoaded(true);
    }
    load().catch((err) => setOnError(err?.message ?? String(err)));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationId]);

  if (!loaded) {
    return <IonSpinner name="bubbles" />;
  }

  // Billing/shipping address and geoLocation became required after this
  // feature first shipped -- older records can predate them and have none
  // on file. The Yup schema still enforces them on every save (a deliberate
  // forcing function, not a bug), so a record missing any of these can't be
  // saved until it's filled in, even for an otherwise-unrelated edit. Make
  // that explicit here rather than leaving the salesperson to guess why
  // "Save Changes" silently won't submit.
  const missingRequired: string[] = [];
  if (!formik.values.geoLocation) missingRequired.push("GPS location");
  if (!formik.values.billingAddress) missingRequired.push("billing address");
  if (!formik.values.shippingAddress) missingRequired.push("shipping address");

  return (
    <form onSubmit={formik.handleSubmit}>
      <IonList>
        {missingRequired.length > 0 && (
          <IonItem lines="none" color="warning">
            <IonLabel class="ion-text-wrap">
              This record is missing required info from before it was mandatory: {missingRequired.join(", ")}.
              You'll need to fill {missingRequired.length > 1 ? "these in" : "this in"} before any changes here can
              be saved.
            </IonLabel>
          </IonItem>
        )}

        <IonItem lines="none">
          <IonLabel>
            Owner
            <p>
              {ownerName} {ownerMobile && `· ${ownerMobile}`}
            </p>
          </IonLabel>
        </IonItem>

        {savedWhatsappNo && <RetailerOnboardingQr whatsappNo={savedWhatsappNo} businessName={formik.values.businessName} />}

        <ChannelPartnerFields formik={formik} isDistributor={isDistributor} />
        <ChannelPartnerMediaUploads
          locationId={locationId}
          existingPhotoUrl={existingPhotoUrl}
          existingGstUrl={existingGstUrl}
          onSuccess={setOnSuccess}
          onError={setOnError}
        />
      </IonList>

      <IonButton expand="block" type="submit" disabled={saving}>
        {saving ? <IonSpinner name="bubbles" /> : "Save Changes"}
      </IonButton>

      <IonToast isOpen={!!onSuccess} onDidDismiss={() => setOnSuccess("")} message={onSuccess} duration={2000} color="success" />
      <IonToast isOpen={!!onError} onDidDismiss={() => setOnError("")} message={onError} duration={2500} color="danger" />
    </form>
  );
};

export default EditInventoryLocation;
